import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Key,
  Globe,
  Lightning,
  CheckCircle,
  GitBranch,
  Hourglass,
  Plugs,
  Plant,
  MagicWand,
  ArrowLeft,
  CaretRight,
} from '@phosphor-icons/react';
import { NODE_TYPE_LABEL } from '@chatsdet/types';
import { Button, Select, TextArea, TextField, Text, LoadingOrbit } from '@chatsdet/ui';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';
import { usePropertyCredentialSummaries } from '../../../hooks/usePropertyMetadata';
import { CredentialSelect } from '../../components/CredentialSelect';
import {
  useNotificationDeliveryHealth,
  hasNoDeliveryChannel,
} from '../../../features/notifications/hooks/useNotificationDeliveryHealth';
import type { WorkflowNodeType } from './helpers';

export type AutobuildSubmitPayload = {
  specText: string;
  propertyId: string;
  credentialId?: string | null;
  startUrl?: string | null;
  name?: string;
};

interface StarterPickerProps {
  onSelectNodeType: (nodeType: WorkflowNodeType) => void;
  isDisabled?: boolean;
  hasApiCatalog?: boolean;
  /** Gated on the `specReview` org feature — hidden entirely (picker renders exactly as today) when off. */
  autobuildEnabled?: boolean;
  /** Owned by FlowDesigner.tsx: lazily creates the draft workflow (if needed), then starts the pipeline. */
  onAutobuildSubmit?: (payload: AutobuildSubmitPayload) => Promise<void>;
  isAutobuildSubmitting?: boolean;
}

const SPEC_TEXT_MIN_LENGTH = 20;

interface StarterOption {
  type: WorkflowNodeType;
  label: string;
  description: string;
  icon: React.ReactNode;
  iconBg: string;
  badge?: string;
}

const primaryOptions: StarterOption[] = [
  {
    type: 'login',
    label: NODE_TYPE_LABEL.login,
    description: 'Sign in as a test user',
    icon: <Key size={24} weight="duotone" className="text-blue-600" />,
    iconBg: 'bg-blue-500/10',
    badge: 'Most common',
  },
  {
    type: 'navigate',
    label: 'Navigate',
    description: 'Go to a URL directly',
    icon: <Globe size={24} weight="duotone" className="text-green-600" />,
    iconBg: 'bg-green-500/10',
  },
  {
    type: 'seed',
    label: 'Seed Data',
    description: 'Provision test data first',
    icon: <Plant size={24} weight="duotone" className="text-teal-600" />,
    iconBg: 'bg-teal-500/10',
  },
];

const secondaryOptions: StarterOption[] = [
  {
    type: 'action',
    label: 'Action',
    description: 'Click, type, or interact',
    icon: <Lightning size={16} weight="duotone" className="text-purple-600" />,
    iconBg: 'bg-purple-500/10',
  },
  {
    type: 'assertion',
    label: 'Assertion',
    description: 'Check something on the page',
    icon: <CheckCircle size={16} weight="duotone" className="text-orange-600" />,
    iconBg: 'bg-orange-500/10',
  },
  {
    type: 'condition',
    label: 'Condition',
    description: 'Branch on if/else',
    icon: <GitBranch size={16} weight="duotone" className="text-cyan-600" />,
    iconBg: 'bg-cyan-500/10',
  },
  {
    type: 'wait',
    label: 'Wait',
    description: 'Pause before continuing',
    icon: <Hourglass size={16} weight="duotone" className="text-amber-700" />,
    iconBg: 'bg-amber-500/10',
  },
];

const apiSequenceOption: StarterOption = {
  type: 'api_sequence',
  label: 'API Sequence',
  description: 'Call an API endpoint',
  icon: <Plugs size={16} weight="duotone" className="text-cyan-600" />,
  iconBg: 'bg-cyan-500/10',
};

export function StarterPicker({
  onSelectNodeType,
  isDisabled,
  hasApiCatalog,
  autobuildEnabled,
  onAutobuildSubmit,
  isAutobuildSubmitting,
}: StarterPickerProps) {
  const allSecondary = hasApiCatalog
    ? [...secondaryOptions, apiSequenceOption]
    : secondaryOptions;

  const [mode, setMode] = React.useState<'pick' | 'compose'>('pick');
  const [name, setName] = React.useState('');
  const [specText, setSpecText] = React.useState('');
  const [propertyId, setPropertyId] = React.useState<string | null>(null);
  const [credentialId, setCredentialId] = React.useState<string | null>(null);
  // Tracks a DELIBERATE "review signed out" choice, distinct from
  // `credentialId === null` meaning "not chosen yet" — see the credential
  // resolution effect below and CredentialSelect's `showSignedOutOption`.
  const [isSignedOutSelected, setIsSignedOutSelected] = React.useState(false);
  const [startUrl, setStartUrl] = React.useState('');
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const { propertyOptions, propertiesQuery } = useOrgPropertySelection();
  // Prefetch delivery health as soon as the composer opens so the submit
  // click handler can read it synchronously from cache (a `window.open` after
  // an `await` gets blocked by most browsers' popup blockers).
  const deliveryHealthQuery = useNotificationDeliveryHealth({ enabled: mode === 'compose' });

  // Credential resolution: the credential chosen here is the identity the
  // spec review signs in as AND the identity the authoring agent builds the
  // flow's login node from, so it must never silently default to signed out.
  // This query shares its cache entry (`['property-credentials', propertyId]`)
  // with the one `CredentialSelect` runs internally — no extra network call.
  const credentialsQuery = usePropertyCredentialSummaries(propertyId, {
    enabled: Boolean(propertyId),
  });
  // Applies the one-time auto-resolution (default / sole credential / no
  // credentials at all) exactly once per property selection, so it never
  // clobbers a choice the user makes afterward.
  const defaultingAppliedForPropertyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!propertyId) return;
    if (credentialsQuery.isLoading) return;
    if (defaultingAppliedForPropertyRef.current === propertyId) return;
    defaultingAppliedForPropertyRef.current = propertyId;

    const credentials = credentialsQuery.data?.items ?? [];
    if (credentials.length === 0) {
      setCredentialId(null);
      setIsSignedOutSelected(true);
      return;
    }
    const preferred =
      credentials.find((credential) => credential.isDefault) ??
      (credentials.length === 1 ? credentials[0] : undefined);
    if (preferred) {
      setCredentialId(preferred.id);
      setIsSignedOutSelected(false);
    }
    // else: several credentials, none default — leave unset. The user must
    // explicitly choose; Start Autobuild stays disabled until they do.
  }, [propertyId, credentialsQuery.isLoading, credentialsQuery.data]);

  // Nothing has been resolved yet: neither a real credential nor a
  // deliberate signed-out choice. Gates submission the same way the
  // Application field already gates it while unset.
  const credentialUnresolved = Boolean(propertyId) && !isSignedOutSelected && !credentialId;

  const showAutobuildCard = Boolean(autobuildEnabled) && mode === 'pick';

  const handleBack = () => {
    setMode('pick');
    setSubmitError(null);
  };

  const handleSubmit = () => {
    if (!onAutobuildSubmit) return;
    if (specText.trim().length < SPEC_TEXT_MIN_LENGTH || !propertyId || credentialUnresolved) return;

    // Synchronous, pre-await popup-blocker-safe check: if we already know the
    // viewer has no configured delivery channel, open settings in a new tab
    // right away — the autobuild run notifies on completion, so a viewer with
    // no channel would otherwise never learn the outcome.
    if (hasNoDeliveryChannel(deliveryHealthQuery.data?.data)) {
      window.open('/settings/notifications', '_blank');
    }

    setSubmitError(null);
    onAutobuildSubmit({
      specText: specText.trim(),
      propertyId,
      credentialId,
      startUrl: startUrl.trim() || null,
      name: name.trim() || undefined,
    }).catch((error: unknown) => {
      setSubmitError(error instanceof Error ? error.message : 'Failed to start Autobuild');
    });
  };

  // `max-h-full` binds the card to the canvas overlay, not the viewport: the
  // canvas is shorter than the window, so a vh cap would let the card grow past
  // it and scroll the header out of reach instead of scrolling the body.
  return (
    <motion.div
      layout
      className="flex flex-col max-h-full my-auto overflow-hidden rounded-xl border border-border bg-background shadow-lg max-w-lg w-full"
    >
      <AnimatePresence mode="wait" initial={false}>
        {mode === 'pick' ? (
          <motion.div
            key="pick"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-col items-center gap-5 w-full p-6 overflow-y-auto"
          >
            <h3 className="text-base font-semibold text-foreground">
              How should this workflow start?
            </h3>

            {showAutobuildCard && (
              <motion.button
                layoutId="autobuild-card"
                type="button"
                disabled={isDisabled}
                onClick={() => setMode('compose')}
                className="group relative flex w-full items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4 text-left transition-colors hover:border-primary hover:bg-primary/10 disabled:opacity-50 disabled:pointer-events-none"
              >
                <span className="absolute -top-2.5 left-4 whitespace-nowrap rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                  Recommended
                </span>
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/15">
                  <MagicWand size={22} weight="duotone" className="text-primary" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">
                    Autobuild from a spec
                  </span>
                  <span className="block text-xs text-muted-foreground leading-tight">
                    Paste a test spec — Cofactor reviews it live, then authors and converges the
                    flow for you
                  </span>
                </span>
                <CaretRight
                  size={16}
                  className="flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                />
              </motion.button>
            )}

            {/* Primary options — large cards */}
            <div className="flex gap-3 w-full">
              {primaryOptions.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => onSelectNodeType(opt.type)}
                  className="group relative flex-1 flex flex-col items-center gap-2 rounded-lg border border-border p-4 text-center transition-colors hover:border-primary/50 hover:bg-accent/50 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {opt.badge && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                      {opt.badge}
                    </span>
                  )}
                  <span
                    className={`flex items-center justify-center w-10 h-10 rounded-lg ${opt.iconBg}`}
                  >
                    {opt.icon}
                  </span>
                  <span className="text-sm font-medium text-foreground">{opt.label}</span>
                  <span className="text-xs text-muted-foreground leading-tight">
                    {opt.description}
                  </span>
                </button>
              ))}
            </div>

            {/* Secondary options — compact row */}
            <div className="flex flex-wrap justify-center gap-2">
              {allSecondary.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => onSelectNodeType(opt.type)}
                  className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground hover:bg-accent/50 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="compose"
            layoutId="autobuild-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex min-h-0 flex-1 flex-col w-full"
          >
            <div className="flex shrink-0 items-center gap-2 px-6 pb-4 pt-6">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/15">
                <MagicWand size={18} weight="duotone" className="text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-foreground">Autobuild from a spec</h3>
                <Text className="text-xs text-muted-foreground">
                  We&apos;ll review it live, then author and converge the flow.
                </Text>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-4">
              <TextField
                label="Flow name (optional)"
                placeholder="Billing — create, edit, delete an invoice"
                value={name}
                onChange={setName}
                maxLength={80}
              />

              <TextArea
                label="Test spec"
                placeholder="Paste the test spec — steps, assumptions, and expected results in any format."
                value={specText}
                onChange={setSpecText}
                rows={4}
                autoExpand
                maxHeight={280}
                autoFocus
              />

              <Select
                label="Application"
                placeholder="Select an application..."
                items={propertyOptions}
                selectedKey={propertyId}
                onSelectionChange={(key) => {
                  setPropertyId(key ? String(key) : null);
                  setCredentialId(null);
                  setIsSignedOutSelected(false);
                  defaultingAppliedForPropertyRef.current = null;
                }}
                isDisabled={propertiesQuery.isLoading}
                isRequired
              />

              {propertyId && (
                <CredentialSelect
                  propertyId={propertyId}
                  selectedCredentialId={credentialId}
                  onSelect={(id) => {
                    setCredentialId(id);
                    setIsSignedOutSelected(false);
                  }}
                  label="Credential"
                  placeholder="Select a credential…"
                  isRequired
                  showSignedOutOption
                  isSignedOutSelected={isSignedOutSelected}
                  onSelectSignedOut={() => {
                    setCredentialId(null);
                    setIsSignedOutSelected(true);
                  }}
                />
              )}

              <TextField
                label="Start URL (optional)"
                placeholder="Defaults to the application base URL"
                value={startUrl}
                onChange={setStartUrl}
              />

              {submitError && (
                <Text size="sm" className="text-destructive">
                  {submitError}
                </Text>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-6 py-4">
              <Button variant="ghost" size="sm" onPress={handleBack} isDisabled={isAutobuildSubmitting}>
                <ArrowLeft size={14} />
                Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={handleSubmit}
                isDisabled={
                  isAutobuildSubmitting ||
                  specText.trim().length < SPEC_TEXT_MIN_LENGTH ||
                  !propertyId ||
                  credentialUnresolved
                }
              >
                {isAutobuildSubmitting ? <LoadingOrbit size="sm" /> : null}
                Start Autobuild
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
